import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveSeoData } from '@/seo/seoData';
import { applyHead } from '@/seo/headManager';
import {
  buildFaqPageSchema,
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildArticleSchema,
  buildBreadcrumbListSchema,
  buildWebPageSchema,
  injectJsonLd,
  clearJsonLd,
} from '@/seo/schema';
import { SITE_ORIGIN, SITE_NAME } from '@/seo/config';
import {
  articleContents,
  articleContentsB,
} from '@/mocks/articleContents';

const allContents = { ...articleContents, ...articleContentsB };

export default function SeoManager() {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const data = resolveSeoData(location.pathname);

    applyHead({
      title: data.title,
      description: data.description,
      keywords: data.keywords,
      canonicalUrl: data.canonicalUrl,
      type: data.type,
      imageUrl: data.imageUrl,
      noindex: data.noindex,
    });

    clearJsonLd();

    if (data.pageType === 'home') {
      injectJsonLd('ld-organization', buildOrganizationSchema());
      injectJsonLd('ld-website', buildWebSiteSchema());

      const faqs = [];
      for (let i = 1; i <= 10; i += 1) {
        faqs.push({ q: t(`faq.q${i}`), a: t(`faq.a${i}`) });
      }
      injectJsonLd('ld-faq', buildFaqPageSchema(faqs));
    } else if (data.pageType === 'article' && data.articleSlug) {
      const content = allContents[data.articleSlug];
      if (content) {
        injectJsonLd(
          'ld-article',
          buildArticleSchema({
            title: content.title,
            description: content.quickAnswer,
            url: data.canonicalUrl,
            imageUrl: content.imageUrl,
            datePublished: content.lastUpdated,
            dateModified: content.lastUpdated,
            category: content.category,
          }),
        );

        injectJsonLd(
          'ld-breadcrumb',
          buildBreadcrumbListSchema([
            { name: SITE_NAME, url: SITE_ORIGIN },
            { name: t('nav.articles'), url: `${SITE_ORIGIN}/pt/articles` },
            { name: content.category, url: data.canonicalUrl },
          ]),
        );
      }
    } else if (data.pageType === 'articles-list') {
      injectJsonLd('ld-website', buildWebSiteSchema());
    } else if (data.pageType === 'page') {
      injectJsonLd(
        'ld-webpage',
        buildWebPageSchema({
          title: data.title,
          description: data.description,
          url: data.canonicalUrl,
        }),
      );
      injectJsonLd('ld-website', buildWebSiteSchema());
    }
  }, [location.pathname, i18n.language, t]);

  return null;
}